import { useState, useEffect, useCallback } from 'react';
import {
  Database,
  Play,
  Loader2,
  AlertCircle,
  CheckCircle2,
  History,
  X,
} from 'lucide-react';
import { SqlEditor, ResultsTable, TableBrowser } from '../../components/SqlEditor';
import { useDangerZone } from '../../contexts/ReauthContext';

// Types
interface ColumnInfo {
  name: string;
  type: string;
  displaySize: number;
  nullable: boolean;
}

interface SqlExecuteResponse {
  success: boolean;
  queryType: string;
  message?: string;
  columns?: ColumnInfo[];
  rows?: Record<string, unknown>[];
  rowCount?: number;
  truncated?: boolean;
  affectedRows?: number;
  executionTimeMs: number;
  executedQuery: string;
}

interface TableSchema {
  tableName: string;
  tableType: string;
  rowCount: number;
  columns: Array<{
    columnName: string;
    dataType: string;
    columnType: string;
    nullable: boolean;
    columnKey: string;
    defaultValue: string | null;
    extra: string;
  }>;
}

interface QueryHistoryItem {
  query: string;
  timestamp: Date;
  success: boolean;
  rowCount?: number;
  executionTimeMs?: number;
}

// API helper
async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `HTTP ${response.status}`);
  }

  return response.json();
}

export function SqlEditorPage() {
  // Query state
  const [query, setQuery] = useState('SELECT * FROM tark_user LIMIT 10;');
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<SqlExecuteResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Tables state
  const [tables, setTables] = useState<TableSchema[]>([]);
  const [isLoadingTables, setIsLoadingTables] = useState(true);

  // History state
  const [history, setHistory] = useState<QueryHistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // Load tables on mount
  useEffect(() => {
    loadTables();
  }, []);

  // Load tables from API
  const loadTables = async () => {
    setIsLoadingTables(true);
    try {
      const data = await fetchApi<TableSchema[]>('/api/v1/admin/sql/tables');
      setTables(data);
    } catch (err) {
      console.error('Failed to load tables:', err);
      // Use mock data for development
      setTables([
        {
          tableName: 'tark_user',
          tableType: 'TABLE',
          rowCount: 1234,
          columns: [
            { columnName: 'id', dataType: 'bigint', columnType: 'bigint(20)', nullable: false, columnKey: 'PRI', defaultValue: null, extra: 'auto_increment' },
            { columnName: 'email', dataType: 'varchar', columnType: 'varchar(255)', nullable: false, columnKey: 'UNI', defaultValue: null, extra: '' },
            { columnName: 'first_name', dataType: 'varchar', columnType: 'varchar(100)', nullable: true, columnKey: '', defaultValue: null, extra: '' },
            { columnName: 'created_at', dataType: 'datetime', columnType: 'datetime', nullable: false, columnKey: '', defaultValue: 'CURRENT_TIMESTAMP', extra: '' },
          ],
        },
        {
          tableName: 'tark_squad',
          tableType: 'TABLE',
          rowCount: 456,
          columns: [
            { columnName: 'id', dataType: 'bigint', columnType: 'bigint(20)', nullable: false, columnKey: 'PRI', defaultValue: null, extra: 'auto_increment' },
            { columnName: 'name', dataType: 'varchar', columnType: 'varchar(255)', nullable: false, columnKey: '', defaultValue: null, extra: '' },
          ],
        },
      ]);
    } finally {
      setIsLoadingTables(false);
    }
  };

  // Danger zone hook for reauth
  const { executeDangerZone } = useDangerZone();

  // Execute query with danger zone protection
  const executeQuery = useCallback(async () => {
    if (!query.trim()) return;

    setIsExecuting(true);
    setError(null);
    setResult(null);

    try {
      // Use danger zone to ensure reauth before executing SQL
      const response = await executeDangerZone<SqlExecuteResponse>(async (headers) => {
        const res = await fetch('/api/v1/admin/sql/execute', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...headers,
          },
          body: JSON.stringify({
            query: query.trim(),
            maxRows: 1000,
            timeoutSeconds: 30,
          }),
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          // Check if reauth required
          if (errorData.error === 'REAUTH_REQUIRED') {
            throw new Error('REAUTH_REQUIRED');
          }
          throw new Error(errorData.message || `HTTP ${res.status}`);
        }

        return res.json();
      });

      setResult(response);

      // Add to history
      setHistory((prev) => [
        {
          query: query.trim(),
          timestamp: new Date(),
          success: response.success,
          rowCount: response.rowCount,
          executionTimeMs: response.executionTimeMs,
        },
        ...prev.slice(0, 49), // Keep last 50 queries
      ]);

      if (!response.success) {
        setError(response.message || 'Query failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to execute query';

      // Don't add cancelled reauth to history
      if (errorMessage === 'Re-authentication cancelled') {
        setError(null);
        setIsExecuting(false);
        return;
      }

      setError(errorMessage);

      // Add failed query to history
      setHistory((prev) => [
        {
          query: query.trim(),
          timestamp: new Date(),
          success: false,
        },
        ...prev.slice(0, 49),
      ]);
    } finally {
      setIsExecuting(false);
    }
  }, [query, executeDangerZone]);

  // Handle table selection from browser
  const handleSelectTable = (tableName: string) => {
    setQuery(`SELECT * FROM ${tableName} LIMIT 100;`);
  };

  // Insert text at cursor (or append)
  const handleInsertTableName = (text: string) => {
    setQuery((prev) => prev + text);
  };

  // Load query from history
  const loadFromHistory = (historyItem: QueryHistoryItem) => {
    setQuery(historyItem.query);
    setShowHistory(false);
  };

  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Database className="h-7 w-7 text-cyan-400" />
            playtark
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            MySQL Database Query Editor
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
              showHistory
                ? 'bg-cyan-600 text-white'
                : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
            }`}
          >
            <History className="h-4 w-4" />
            History
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Left panel - Table browser */}
        <div className="w-64 flex-shrink-0">
          <TableBrowser
            tables={tables}
            isLoading={isLoadingTables}
            onRefresh={loadTables}
            onSelectTable={handleSelectTable}
            onInsertTableName={handleInsertTableName}
          />
        </div>

        {/* Center panel - Editor and results */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Editor */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-400">Query</span>
              <button
                onClick={executeQuery}
                disabled={isExecuting || !query.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
              >
                {isExecuting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Executing...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Execute (Ctrl+Enter)
                  </>
                )}
              </button>
            </div>
            <SqlEditor
              value={query}
              onChange={setQuery}
              onExecute={executeQuery}
              height="200px"
            />
          </div>

          {/* Results */}
          <div className="flex-1 min-h-0 flex flex-col rounded-lg border border-slate-800 bg-slate-900/30">
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-b border-slate-800">
              <span className="text-sm font-medium text-slate-400">Results</span>
              {result && (
                <div className="flex items-center gap-2">
                  {result.success ? (
                    <CheckCircle2 className="h-4 w-4 text-green-400" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-400" />
                  )}
                  <span className="text-xs text-slate-500">
                    {result.queryType} • {result.executionTimeMs}ms
                  </span>
                </div>
              )}
            </div>

            <div className="flex-1 min-h-0 p-4">
              {error && (
                <div className="flex items-start gap-3 p-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400">
                  <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium">Error</div>
                    <div className="text-sm text-red-400/80 mt-1">{error}</div>
                  </div>
                </div>
              )}

              {result?.success && result.queryType === 'SELECT' && result.columns && result.rows && (
                <ResultsTable
                  columns={result.columns}
                  rows={result.rows}
                  truncated={result.truncated}
                  executionTimeMs={result.executionTimeMs}
                  rowCount={result.rowCount}
                />
              )}

              {result?.success && ['INSERT', 'UPDATE', 'DELETE'].includes(result.queryType) && (
                <div className="flex items-center gap-3 p-4 bg-green-900/20 border border-green-800/50 rounded-lg text-green-400">
                  <CheckCircle2 className="h-5 w-5" />
                  <div>
                    <div className="font-medium">Query executed successfully</div>
                    <div className="text-sm text-green-400/80 mt-1">
                      {result.affectedRows} row(s) affected in {result.executionTimeMs}ms
                    </div>
                  </div>
                </div>
              )}

              {!result && !error && (
                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                  <Database className="h-12 w-12 mb-3 opacity-50" />
                  <p>Execute a query to see results</p>
                  <p className="text-xs mt-1">Press Ctrl+Enter or click Execute</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right panel - History */}
        {showHistory && (
          <div className="w-80 flex-shrink-0 flex flex-col bg-slate-900/50 border border-slate-800 rounded-lg">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
              <span className="text-sm font-medium text-slate-300">Query History</span>
              <button
                onClick={() => setShowHistory(false)}
                className="p-1 rounded hover:bg-slate-800 text-slate-400"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {history.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-500">
                  No queries yet
                </div>
              ) : (
                <div className="py-1">
                  {history.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => loadFromHistory(item)}
                      className="px-3 py-2 hover:bg-slate-800/50 cursor-pointer border-b border-slate-800/50"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {item.success ? (
                          <CheckCircle2 className="h-3 w-3 text-green-400" />
                        ) : (
                          <AlertCircle className="h-3 w-3 text-red-400" />
                        )}
                        <span className="text-xs text-slate-500">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                        {item.rowCount !== undefined && (
                          <span className="text-xs text-slate-600">
                            {item.rowCount} rows
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 font-mono truncate">
                        {item.query}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {history.length > 0 && (
              <div className="px-3 py-2 border-t border-slate-800">
                <button
                  onClick={() => setHistory([])}
                  className="text-xs text-slate-500 hover:text-slate-400"
                >
                  Clear history
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
